import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from  './AnalysisPage.module.scss';

import AnalysisLayout from './rendering/AnalysisLayout';
import AnalysisList from './analysisList/AnalysisList';
import AnalysisTool from './analysisTool';

import { useAnalysisPage } from './useAnalysisPage';
import {
  AnalysisType,
  getSegShapeAnalysisType,
  SegmentationControllerType,
} from 'config/analysisConfig';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { Series } from '@models/Series';
import { useAuthPage } from '@features/auth/useAuthPage';
import { usePanelContainer } from './rendering/Panels/usePanelContainer';
import {
  addCornerstoneCoreEvents,
  addRequestPool,
  removeCornerstoneCoreEvents,
  setup,
  updateLabelmapSegment,
  volumeCacheVolumeAddedEvent,
} from './rendering/Panels/Renderers/CornerstoneManager';
import { useQuery } from '@tanstack/react-query';
import { getSeriesByStudy } from '@api/series';
import { toCamelCase } from 'utils/caseConverter';
import { useModal } from '@components/Modal/ModalProvider';
import Modal from '@components/Modal';
import ResponseWaiting from '@components/ResponseWaiting/ResponseWaiting';
import {
  registerOriginalVolumeBufferPool,
  useBufferPool,
} from './rendering/Panels/Renderers/vtkManagers';
import { useHotkeys } from '@hooks/useHotKeys';
import { hotkeysConfigAnalysis, hotkeysUpConfigAnalysis } from 'config/binding';
import { ICON_MAP } from '@components/Icons/Icons';
import { useTool } from '@hooks/Tool/useTool';
import { scheduleRender } from './rendering/Panels/renderState';
import useModalActions from '@hooks/common/useModalAction';
import { useConfig } from 'config/useConfig';
import { fetchSeriesDicomTags } from '@api/files';
import { DICOM_TAGS } from 'config/dicomTags';
import { SegmentManagerList } from './analysisList/SegmentManagerList';
import { useSegmentManager } from './segmentManager';
import { fetch_base_info, getAnalysisStatusByName, getDefaultItem, SeriesAnalysisStatusMap } from 'utils/AnalysisBaseUtilities/AnalysisBaseUtilities';
import { useAnalysisBaseUtilities } from 'utils/AnalysisBaseUtilities/useAnalysisBaseUtilities';

const sampleAllowedAnalysis: AnalysisType[] = [
  'MPR',
  'segmentation',
  // 'cpr',
  'wallthickness',
  'aorticValveCalcium',
  'calciumScore',
  'ejectionFraction',
  'fai',
  'isr',
  'plaque',
  'stenosis',
  'chamber',
  'myocardial',
  'tavi',
  'FFR',
  // 'xa-analysis2',
];

const AnalysisPage: React.FC = (): React.JSX.Element => {

  const Back = ICON_MAP['back'];
  const Show = ICON_MAP['show'];
  const Hide = ICON_MAP['hide'];

  const { accessToken } = useAuthPage();
  const {
    selectedStudy,
    selectedStudyId,
    selectedSeriesId,
    selectedSeries,
    selectedStudySeries: currentSeries,
    defaultPanelStates,
    updateSeries,
  } = useBrowserPage();
  const {
    switchConfigs,
    selectedLayoutConfig,
    handleResetLayoutConfig,
    handleLayoutStateLoading,
    isLayoutStateLoading,
    globalToolState,
    panelToolState,
    handleResetToolState,
    handleUpdateToolForPanel,
    handleUpdateGlobalTool,
  } = useAnalysisPage();
  const {
    isCornerstoneInitialized,
    selectedPanelComponentKey,
    setCornerstoneInitializedFlag,
    analysisType,
    panelStates,
    updatePanelState,
    resetPanelStates,
  } = usePanelContainer();
  // const { general } = useSettingsModal();

  const {mode: manualMode,setMode} = useSegmentManager();
  const { handleToolActivate } = useTool();
  const { contextPool, bufferPool } = useBufferPool();
  const { openModal, closeModal } = useModal();
  const { config,getColors,setData } = useConfig();
  const isEditMode = config.EditMode !== null;
  // const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // const toggleSidebar = () => setIsSidebarCollapsed((prev) => !prev);
  const [isSegmentationAllOff, setIsSegmentationAllOff] = useState(false);
  const [analysisBaseInfoBySeries, setAnalysisBaseInfoBySeries] = useState<SeriesAnalysisStatusMap>({});
  const [completedModifyRefreshSeriesId, setCompletedModifyRefreshSeriesId] =
    useState<string | null>(null);
  const analysisBaseInfoBySeriesRef = useRef(analysisBaseInfoBySeries);
  const pendingModifyBySeriesRef = useRef<Record<string, boolean>>({});
  const seriesSelectionLoadingModalIdRef = useRef<string | null>(null);
  const seriesSelectionLoadingCloseTimerRef = useRef<number | null>(null);

  const { renderingQuality } = config.generalSetting;

  const { segmentation } = panelStates['Surface3D'];

  const hasVR = selectedLayoutConfig.panelConfig.some((panel) => panel.componentKey === 'VR',);
  const isSegLabelsVisible =
    (['FFR','chamber'].includes(analysisType) || config.EditMode !== null);
  const isMIPEnabled =
    config.visibleMIP && getSegShapeAnalysisType(analysisType);

  const { waitingAction, closeLoadingModal, selectIconAction } = useModalActions();

  const { refreshData } = useAnalysisBaseUtilities();

  async function toggleSegmentation() {
    if (!segmentation) return;
    const segKeys = Object.keys(segmentation) as SegmentationControllerType[];
    const newSegmentation = { ...segmentation };

    waitingAction({ message: 'Waiting' });

    for (const key of segKeys) {
      newSegmentation[key] = {
        ...newSegmentation[key],
        opacity:
          newSegmentation[key].opacity === 0 ? 1 : newSegmentation[key].opacity,
        visibility: !isSegmentationAllOff,
      };
      updatePanelState(key, 'segmentation', {
        ...newSegmentation,
      });

      updateLabelmapSegment({
        segmentTarget: key,
        segmentations: newSegmentation,
      });
    }

    if (hasVR) {
      scheduleRender('VR', { type: 'updateSegmentation', newSegmentation });
    } else {
      scheduleRender('Surface3D', { type: 'updateLabelmap' });
    }
    setIsSegmentationAllOff((prev) => {
      return !prev;
    });

    closeLoadingModal();
  }


  // binding.ts 에 AnalysisHotkeyCtx 추가했으면 여기 추가하기
  // 위치는 여기가 맞는지는 추후 고려해봐야함
  // 현재까지는 문제없지만 input 데이터에 따라 위치 변경해야할 수도 있음
  useHotkeys(hotkeysConfigAnalysis, {
    analysisType: analysisType,
    context: contextPool,
    selectedPanelComponentKey: selectedPanelComponentKey!,
    activeToolId: panelToolState?.activeToolId,
    handleToolActivate,
  }, hotkeysUpConfigAnalysis);

  const isSelectedSeriesDataLoading = useMemo(() => {
    if (!selectedSeriesId) return false;
    const selectedBuffer = bufferPool[selectedSeriesId];
    const isSelectedBufferReady = !!selectedBuffer?.parameter?.loaded;
    return isLayoutStateLoading || !isSelectedBufferReady;
  }, [bufferPool, isLayoutStateLoading, selectedSeriesId]);

  useEffect(() => {
    if (seriesSelectionLoadingCloseTimerRef.current !== null) {
      window.clearTimeout(seriesSelectionLoadingCloseTimerRef.current);
      seriesSelectionLoadingCloseTimerRef.current = null;
    }

    if (isSelectedSeriesDataLoading) {
      if (seriesSelectionLoadingModalIdRef.current) return;

      const id = openModal(
        <Modal
          type="page"
          close={() => undefined}
          description={<ResponseWaiting type="download" message="Loading data" />}
        />,
        false,
        undefined,
        true,
      );
      seriesSelectionLoadingModalIdRef.current = id;
      return;
    }

    if (!seriesSelectionLoadingModalIdRef.current) return;

    const modalId = seriesSelectionLoadingModalIdRef.current;
    seriesSelectionLoadingCloseTimerRef.current = window.setTimeout(() => {
      closeModal(modalId);
      if (seriesSelectionLoadingModalIdRef.current === modalId) {
        seriesSelectionLoadingModalIdRef.current = null;
      }
      seriesSelectionLoadingCloseTimerRef.current = null;
    }, 300);
  }, [closeModal, isSelectedSeriesDataLoading, openModal]);

  useEffect(() => {
    return () => {
      if (seriesSelectionLoadingCloseTimerRef.current !== null) {
        window.clearTimeout(seriesSelectionLoadingCloseTimerRef.current);
        seriesSelectionLoadingCloseTimerRef.current = null;
      }
      if (seriesSelectionLoadingModalIdRef.current) {
        closeModal(seriesSelectionLoadingModalIdRef.current);
        seriesSelectionLoadingModalIdRef.current = null;
      }
    };
  }, [closeModal]);

  // 쿼리로 서버에서 받아오기.
  // currentSeries가 시리즈를 보유하고 있어도
  // 비교 검증을 위해 일단 실행
    const { data: seriesData = [], isSuccess } = useQuery({
      queryKey: ['studyGroupWithDicom', selectedStudyId],
      queryFn: async () => {
        const resolvedSeries = await getSeriesByStudy({
          accessToken,
          params: { id: selectedStudyId ?? '' },
        });

        const seriesList = toCamelCase<Series[]>(resolvedSeries?.data ?? []);
        const SCAN_OPTIONS_TAG =
          Object.entries(DICOM_TAGS).find(
            ([, name]) => name === 'Scan Options',
          )?.[0] ?? '00180022';

        const dicomResults = await Promise.all(
          seriesList.map((series) =>
            fetchSeriesDicomTags({
              accessToken,
              seriesId: series.id ?? '',
            }),
          ),
        );

        return seriesList.map((series, index) => {
          const dicomTag = dicomResults[index]?.data;
          return {
            ...selectedStudy,
            ...series,
            imageTag: {
              ...series.imageTag,
              windowWidth: (series.imageTag.windowWidth as any)?.[0],
              windowCenter: (series.imageTag.windowCenter as any)?.[0],
              scanOptions: dicomTag?.[SCAN_OPTIONS_TAG]?.Value?.[0] ?? '',
            },
          };
        });
      },
      enabled: !!accessToken && !!selectedStudyId,
      retry: false,
      refetchOnWindowFocus: false,
    });

  // 비교하는것
  const compareSeriesWithCurrent = useCallback(
    (target: Series[]) => {
      if (currentSeries.length !== target.length) return false;
      const setCurrent = new Set(currentSeries.map((s) => s.id));
      const setTarget = new Set(target.map((s) => s.id));
      for (const id of setCurrent) if (!setTarget.has(id)) return false;
      return true;
    },
    [currentSeries],
  );

  // 다르면 업뎃
  useEffect(() => {
    if (!isSuccess) return;
    if (!seriesData.length && !currentSeries.length) return;
    if (!compareSeriesWithCurrent(seriesData)) {
      updateSeries(seriesData);
    }
  }, [isSuccess, currentSeries, seriesData]);

  // 코너스톤 초기화. 엔진은 현재로썬 mainEngineId 한 개만
  useEffect(() => {
    if (!isCornerstoneInitialized) {
      handleLayoutStateLoading(true);
      setup({
        afterInitialize() {
          console.log('Cornerstone 초기화 완료');
          setCornerstoneInitializedFlag(true);
        },
      });

      resetPanelStates({...defaultPanelStates});
    }

    const volumeCacheVolumeAdded = (evt: Event) =>
      volumeCacheVolumeAddedEvent({
        evt,
        callback: registerOriginalVolumeBufferPool,
        callbackTag: renderingQuality === 'high' ? 'ORIGINAL' : 'PREVIEW',
      });
    // afterVolumeLoaded 내부에서 Original 볼륨을 곧바로 vtk에 쏴버리기 위해
    addCornerstoneCoreEvents({ volumeCacheVolumeAdded });
    return () => removeCornerstoneCoreEvents({ volumeCacheVolumeAdded });
  }, [isCornerstoneInitialized]);

  // // 통신 몇가지 설정
  // useEffect(()=>{
  //   if(!isCornerstoneInitialized) return;
  //   initNiftiVolumeLoader(accessToken);
  // },[isCornerstoneInitialized,accessToken])
  useEffect(() => {
    handleResetToolState();
    if(manualMode)
      setMode(false);
  }, [selectedSeriesId]);

  // const isSegmentationShape = getSegShapeAnalysisType(analysisType);
  // const toggleMIP = useMemo(
  //   () =>
  //     isSegmentationShape &&
  //     hasVR &&
  //     config.visibleMIP,
  //   [analysisType, hasVR, config.visibleMIP]
  // );
  // useEffect(() => {
  //   const isMIPActive = globalToolState?.toggles.ToggleMIP
  //   if(toggleMIP && !isMIPActive){
  //     handleToolActivate('ToggleMIP');
  //   }else if (!toggleMIP && isMIPActive) {
  //     handleToolActivate('ToggleMIP');
  //     handleUpdateToolForPanel('VRPanel', 'Rotation');
  //   }

  // }, [toggleMIP,globalToolState?.toggles.ToggleMIP]);

  // 프리뷰도 여기서 하자
  useEffect(() => {
    if (!isCornerstoneInitialized || !selectedStudy || !currentSeries.length)
      return;
    // console.log('프리뷰 따운',isCornerstoneInitialized, selectedStudy,currentSeries)
    // 만약 updateSeries에서 이전 데이터와 다른경우라면, 볼륨캐시가 누적될텐데?
    // updateSeries 리스너를 통해 해결해보자.

    if (analysisType === 'preview' || analysisType === 'empty') {
      handleResetLayoutConfig();
    }

    let remainingPreviewDownloads = currentSeries.length;
    let isBulkPreviewModalClosed = false;
    const id = openModal(
      <Modal
        type="page"
        close={() => closeModal(id)}
        description={<ResponseWaiting type="download" message="Loading data" />}
      />,
      false,
      undefined,
      true,
    );

    const closeBulkPreviewModal = () => {
      if (isBulkPreviewModalClosed) return;
      isBulkPreviewModalClosed = true;
      closeModal(id);
    };

    currentSeries.forEach((series) => {
      addRequestPool({
        accessToken,
        series,
        volumeId: `PREVIEW_${series.id}`,
        filepath: `${series.id}_preview`,
        priority: 0,
        postFunction: () => {
          remainingPreviewDownloads -= 1;
          if (remainingPreviewDownloads > 0) return;

          if (renderingQuality !== 'high') {
            if (analysisType === 'preview' || analysisType === 'MPR') {
              handleLayoutStateLoading(false);
            }
          }
          closeBulkPreviewModal();
        },
      });
    });

    return closeBulkPreviewModal;
  }, [
    isCornerstoneInitialized,
    selectedStudy,
    currentSeries,
    renderingQuality,
    analysisType,
  ]);

  // analysisList와 segmentManager가 공존하지 않음.
  // 그로인해 두 탭간 스위치시, 지속적인 초기화 발생
  // 그 결과는 cpr.coronary 상태의 초기화 = 버그
  // 따라서 AnalysisList말고 밖으로 뺌.
  // 이 행위 자체를 하지않음.
  // 챔버뷰프롭은 지워ㅕ야겠다.
  useEffect(() => {
    // console.log(selectedSeriesId)
    // if (!defaultPanelStates?.Surface3D?.segmentation) return;
    // if (!selectedSeriesId) return;

    // let segmentation = Object.fromEntries(
    //   Object.entries(defaultSegmentationProps).map(([key, value]) => {
    //     return [key, { ...value, color: hexToRgb(getColors(key) as string) }];
    //   }),
    // ) as Segmentation;

    const {chamberViewProps:cvAxial, ...resAxial}=panelStates.Axial
    const {chamberViewProps:cvShort, ...resShort}=panelStates.ShortAxis
    const {chamberViewProps:cvFour, ...resFour}=panelStates.FourChamber
    const {chamberViewProps:cvTwo, ...resTwo}=panelStates.TwoChamber
    resetPanelStates({
      Axial:resAxial,
      ShortAxis:resShort,
      FourChamber:resFour,
      TwoChamber:resTwo
    });
  }, [selectedSeriesId, defaultPanelStates]);




  useEffect(() => {
    if (!accessToken || currentSeries.length === 0) return;

    let active = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const isDoneStatus = (status: unknown) =>
      status === 'Succeeded' || status === 'Failed';
    const isModifyName = (name: string) =>
      name === 'modify-lumen' || name === 'modify-regulator';

    const poll = async () => {
      const currentMap = analysisBaseInfoBySeriesRef.current;
      const seriesToFetch = currentSeries.filter((series) => {
        const items = currentMap[series.id] ?? [];
        const status = getDefaultItem(items)?.status;


        const defaultDone = isDoneStatus(status);
        const hasPendingModify = items.some(
          (item) => {
            const name = item.name.toLowerCase();
            const isModify = isModifyName(name);

            return isModify && !isDoneStatus(item.status);
          },
        );
        return !defaultDone || hasPendingModify;
      });
      if (seriesToFetch.length === 0) {
        return
      };

      const results = await Promise.all(
        seriesToFetch.map(async (series) => {
          const items = await fetch_base_info(accessToken, series.id);
          return [series.id, items] as const;
        }),
      );
      if (!active) return;
      setAnalysisBaseInfoBySeries((prev) => {
        const next = { ...prev };
        for (const [seriesId, rawBaseInfo] of results) {
          const items = Array.isArray(rawBaseInfo) ? rawBaseInfo : [];
          next[seriesId] = items;
        }
        analysisBaseInfoBySeriesRef.current = next;
        return next;
      });
      // const next = { ...analysisBaseInfoBySeriesRef.current };
      // let shouldSetModifyFFR: boolean | undefined;
      // let completedModifySeriesId: string | null = null;

      // for (const [seriesId, rawBaseInfo] of results) {
      //   const items = Array.isArray(rawBaseInfo) ? rawBaseInfo : [];
      //   next[seriesId] = items;

      //   const modifyItems = items.filter((item) =>
      //     isModifyName(item.name.toLowerCase()),
      //   );
      //   const hasPendingModify = modifyItems.some(
      //     (item) => !isDoneStatus(item.status),
      //   );
      //   const modifyDone =
      //     modifyItems.length > 0 &&
      //     modifyItems.every((item) => isDoneStatus(item.status));

      //   if (modifyItems.length > 0) {
      //     const wasPendingModify = pendingModifyBySeriesRef.current[seriesId];

      //     if (hasPendingModify) {
      //       shouldSetModifyFFR = true;
      //     }
      //     if (wasPendingModify && modifyDone) {
      //       shouldSetModifyFFR = false;
      //       completedModifySeriesId = seriesId;
      //     }
      //     pendingModifyBySeriesRef.current[seriesId] = hasPendingModify;
      //   }
      // }

      // analysisBaseInfoBySeriesRef.current = next;
      // setAnalysisBaseInfoBySeries(next);
      // if (shouldSetModifyFFR !== undefined) {
      //   setData({ modifyRun: shouldSetModifyFFR });
      // }
      // if (completedModifySeriesId) {
      //   setCompletedModifyRefreshSeriesId(completedModifySeriesId);
      // }
    };
    poll();
    intervalId = setInterval(poll, 5000);
    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [accessToken, currentSeries]);


  useEffect(() => {
    if (!selectedSeriesId || !analysisBaseInfoBySeries) return;
    const items = analysisBaseInfoBySeries[selectedSeriesId];
    if (!items) return;

    const isDoneStatus = (status: unknown) =>
      status === 'Succeeded' || status === 'Failed';
    const isModifyName = (name: string) =>
      name === 'modify-lumen' || name === 'modify-regulator';

    const modifyItems = items.filter((item) =>
      isModifyName(item.name.toLowerCase()),
    );

    const hasPendingModify = modifyItems.some(
      (item) => !isDoneStatus(item.status),
    );

    const wasPendingModify =
      pendingModifyBySeriesRef.current[selectedSeriesId] ?? false;

    const modifyDone =
      modifyItems.length > 0 &&
      modifyItems.every((item) => isDoneStatus(item.status));

    setData({ modifyRun: hasPendingModify });

    if (wasPendingModify && modifyDone) {
      selectIconAction({
        title: `Modification is complete. Refreshing the data to display the updated analysis results.`,
        actionRight: refreshData,
        options: { runActionOnClose: true, Icon: 'SuccessIcon' },
      });
    }
    console.log({
      wasPendingModify,
      modifyDone,
      modifyItems,
      hasPendingModify,
      modifyRun: config.modifyRun,
    });
    pendingModifyBySeriesRef.current[selectedSeriesId] = hasPendingModify
  }, [
    analysisBaseInfoBySeries,
    selectedSeriesId,
  ]);

  // useEffect(() => {
  //   if(!selectedSeriesId || !selectedSeries || !completedModifyRefreshSeriesId) return;
  //   if (completedModifyRefreshSeriesId !== selectedSeriesId) return;
  //   if (!bufferPool[selectedSeriesId]) return;
  //   selectIconAction({
  //     title: `Modification is complete. Refreshing the data to display the updated analysis results.`,
  //     actionRight: refreshData,
  //     options: { runActionOnClose: true, Icon: 'SuccessIcon' },
  //   });
  //   setCompletedModifyRefreshSeriesId(null);
  // }, [
  //   completedModifyRefreshSeriesId,
  //   bufferPool,
  //   selectedSeriesId,
  //   selectedSeries,
  // ]);

  useEffect(() => {
    if (!isEditMode) return;
    if (globalToolState?.toggles.ToggleSegmentationManager) return;
    handleUpdateGlobalTool('ToggleSegmentationManager');
  }, [
    globalToolState?.toggles.ToggleSegmentationManager,
    handleUpdateGlobalTool,
    isEditMode,
  ]);

  // console.log()
  const analysisList = useMemo(()=>{
    return(
    <AnalysisList
    seriesList={currentSeries}
    allowedAnalysisIds={sampleAllowedAnalysis}
    analysisBaseInfoBySeries={analysisBaseInfoBySeries}

    />
  )},[currentSeries,sampleAllowedAnalysis,analysisBaseInfoBySeries, setAnalysisBaseInfoBySeries]);
  const segmentManagerList = useMemo(()=>{
    if(!selectedSeriesId) return<></>;
    if(!Object.keys(analysisBaseInfoBySeries).length)return;
    return (<SegmentManagerList
      contextPool={contextPool}
      analysisStatusByName={getAnalysisStatusByName(analysisBaseInfoBySeries[selectedSeriesId])}
      />)
  },[contextPool,analysisBaseInfoBySeries, setAnalysisBaseInfoBySeries]);

  return (
    <div
      className={styles.mainContent}
      // className={`${styles.mainContent}${isSidebarCollapsed ? styles.collapsed : ''}`}
    >
      {/* {!isSidebarCollapsed && ( */}
      <div className={styles.sidebar}>
        <div className={styles.rootColumn}>
          {/* <div className="container-analysis-navbar-top"> */}
          <div className={styles.tableItem}>
            <div
              className={`${styles.studiesContainer} ${manualMode || isEditMode ? styles.disabled : ''}`}
            >
              {globalToolState?.toggles.ToggleSegmentationManager &&
                !isEditMode && (
                  <Back
                    className={styles['container-list-name-button']}
                    onClick={(e) => {
                      if (isEditMode) return;
                      // if(manualMode && dirty){
                      //   open({
                      //     message:'테스트요 ㅎㅎd전부출몰 ㅎ',
                      //     cancelCallback:()=>{console.log('후후2222')},
                      //     discardCallback:()=>{discardSegmentManager()},
                      //     applyCallback:()=>{console.log('속았지')}
                      //   })
                      //   return;
                      // }
                      //기본
                      handleToolActivate('ToggleSegmentationManager');
                    }}
                  />
                )}
              <span className={styles.studiesText}>
                {globalToolState?.toggles.ToggleSegmentationManager
                  ? 'Segment Manager'
                  : 'Series'}
              </span>
            </div>
            <div
              className={`${styles['container-list-name-button']} ${manualMode ? styles.disabled : ''}`}
              onClick={toggleSegmentation}
            >
              {!isSegLabelsVisible &&
                globalToolState?.toggles?.ToggleSegmentationManager &&
                !isMIPEnabled &&
                (isSegmentationAllOff ? <Show /> : <Hide />)}
            </div>
          </div>

          {globalToolState?.toggles.ToggleSegmentationManager
            ? segmentManagerList
            : analysisList}
        </div>

        <AnalysisTool
          seriesList={currentSeries}
          analysisStatusByName={
            selectedSeriesId && analysisBaseInfoBySeries[selectedSeriesId]
              ? getAnalysisStatusByName(
                  analysisBaseInfoBySeries[selectedSeriesId],
                )
              : undefined
          }
        />
      </div>
      {/* )} */}
      {/* {isSidebarCollapsed && (
        <div className="sidebar-restore-trigger" onClick={toggleSidebar}>
          <div className="sidebar-restore-trigger-button">
            <Button
              onClick={() => toggleSidebar}
              className="fullscreen-toggle-btn"
              color="dark"
            >
              {isSidebarCollapsed ? '->' : '<-'}
            </Button>
          </div>
          <div className="sidebar-restore-trigger-toolbox"></div>
        </div>
      )} */}
      <div className={styles.operatorScreen}>
        <AnalysisLayout />
      </div>
    </div>
    // </div>
  );
};

export default AnalysisPage;
